import React from "react";
import { Box, Button, Heading, Stack, Text } from "@chakra-ui/react";
import { OktonAlert } from "@okton/platform-ui";
import * as api from "./aiExecutionApi";
import { ExecutionCapabilities, ExecutionJob, StartExecution } from "./types";
import { requestAiBudget, AiBudgetState } from "../ai-budget/aiBudgetApi";
const waiting = (j: ExecutionJob) =>
  ["awaiting_authorization", "awaiting_generation_authorization"].includes(
    j.state,
  );
const running = (j: ExecutionJob) =>
  waiting(j) || ["counting", "reserved", "running"].includes(j.state);
const labels: Record<ExecutionJob["state"], string> = {
  awaiting_authorization: "Aguardando autorização de contagem",
  counting: "Contando entrada",
  awaiting_generation_authorization: "Aguardando autorização de geração",
  reserved: "Valor reservado",
  running: "Gerando amostra",
  settled: "Consumo apurado",
  uncertain: "Consumo pendente de confirmação",
  cancelled: "Cancelada",
  failed: "Não concluída",
};
type Actions = {
  read: () => Promise<void>;
  start: () => Promise<void>;
  resume: (id: string) => Promise<void>;
  cancel: (id: string) => Promise<void>;
};
export const AiExecutionPanel: React.FC<{
  systemId: string;
  accessToken: string;
  active: boolean;
}> = ({ systemId, accessToken, active }) => {
  const [jobs, setJobs] = React.useState<ExecutionJob[]>([]);
  const [capability, setCapability] =
    React.useState<ExecutionCapabilities | null>(null);
  const [budget, setBudget] = React.useState<AiBudgetState | null>(null);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState("");
  const [message, setMessage] = React.useState("");
  const [uncertain, setUncertain] = React.useState(false);
  const [reconciled, setReconciled] = React.useState(false);
  const pending = React.useRef<StartExecution | null>(null);
  const context = React.useRef("");
  const actions = React.useRef<Actions | null>(null);
  React.useEffect(() => {
    const controller = new AbortController();
    const key = systemId + "\n" + accessToken;
    if (context.current !== key) {
      pending.current = null;
      context.current = key;
    }
    let intent: string | null = null,
      granted: { key: string; at: number } | null = null;
    let intentVersion = 0;
    let locked = false,
      latest: ExecutionJob[] = [],
      config: ExecutionCapabilities | null = null;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const valid = () =>
      !controller.signal.aborted && active && Boolean(accessToken);
    const visible = () => valid() && !document.hidden;
    const fail = (failure: unknown) => {
      intent = null;
      granted = null;
      if (valid())
        setError(
          failure instanceof api.ExecutionError
            ? failure.message
            : "Não foi possível concluir a operação. Recarregue para conferir.",
        );
    };
    const apply = (job: ExecutionJob) => {
      latest = [job, ...latest.filter((j) => j.id !== job.id)].slice(0, 20);
      setJobs(latest);
    };
    const schedule = () => {
      if (timer) clearTimeout(timer);
      if (visible())
        timer = setTimeout(() => {
          void read();
        }, 3000);
    };
    async function read(): Promise<void> {
      if (!visible() || locked) return;
      locked = true;
      setBusy(true);
      try {
        const [caps, list, funds] = await Promise.all([
          api.capabilities(systemId, accessToken, controller.signal),
          api.list(systemId, accessToken, controller.signal),
          requestAiBudget(accessToken, controller.signal),
        ]);
        if (!valid()) return;
        config = caps;
        latest = list;
        setCapability(caps);
        setJobs(list);
        setBudget(funds);
        if (pending.current) {
          const found = list.find(
            (j) => j.requestId === pending.current!.requestId,
          );
          setReconciled(true);
          if (found) {
            pending.current = null;
            setUncertain(false);
            setMessage(
              "Solicitação localizada. Use Continuar para autorizar as próximas etapas.",
            );
          }
        }
        const selected = list.find((j) => j.id === intent);
        if (selected && waiting(selected) && visible() && caps.canStart) {
          const grantKey =
            selected.id + ":" + selected.stage + ":" + selected.stateVersion;
          if (granted?.key !== grantKey) {
            granted = { key: grantKey, at: Date.now() };
            const updated = await api.authorize(
              systemId,
              selected.id,
              selected.stage,
              selected.stateVersion,
              accessToken,
              controller.signal,
            );
            if (valid()) apply(updated);
          } else if (Date.now() - granted.at >= 15000) {
            intent = null;
            granted = null;
            setMessage(
              "A etapa aguarda autorização atual. Use Continuar para retomar.",
            );
          }
        } else if (!selected || !running(selected) || !caps.canStart) {
          intent = null;
          granted = null;
        }
      } catch (failure) {
        fail(failure);
      } finally {
        locked = false;
        if (valid()) {
          setBusy(false);
          schedule();
        }
      }
    }
    async function start(): Promise<void> {
      if (!visible() || locked || !config?.canStart || latest.some(running))
        return;
      locked = true;
      setBusy(true);
      setError("");
      setMessage("");
      const input = pending.current || {
        requestId: api.requestId(),
        templateId: "synthetic-health-v1" as const,
        configurationRevision: config.configurationRevision,
      };
      pending.current = input;
      const startedIntentVersion = intentVersion;
      try {
        const job = await api.start(
          systemId,
          input,
          accessToken,
          controller.signal,
        );
        if (!valid()) return;
        pending.current = null;
        setUncertain(false);
        setReconciled(false);
        apply(job);
        intent =
          startedIntentVersion === intentVersion && visible() ? job.id : null;
        granted = null;
      } catch (failure) {
        if (valid()) {
          const rejected =
            failure instanceof api.ExecutionError &&
            [409, 422].includes(failure.status);
          if (rejected) {
            pending.current = null;
            config = null;
            setCapability(null);
          }
          setUncertain(!rejected);
          setReconciled(false);
        }
        fail(failure);
      } finally {
        locked = false;
        if (valid()) {
          setBusy(false);
          schedule();
        }
      }
    }
    async function resume(id: string): Promise<void> {
      if (
        !visible() ||
        locked ||
        !latest.some((j) => j.id === id && waiting(j))
      )
        return;
      intent = id;
      granted = null;
      setError("");
      setMessage("");
      await read();
    }
    async function cancel(id: string): Promise<void> {
      if (!visible() || locked) return;
      intent = null;
      granted = null;
      locked = true;
      setBusy(true);
      setError("");
      try {
        const job = await api.cancel(
          systemId,
          id,
          accessToken,
          controller.signal,
        );
        if (valid()) apply(job);
      } catch (failure) {
        fail(failure);
      } finally {
        locked = false;
        if (valid()) {
          setBusy(false);
          schedule();
        }
      }
    }
    const visibility = () => {
      intentVersion++;
      intent = null;
      granted = null;
      if (timer) clearTimeout(timer);
      if (visible()) {
        setMessage("Use Continuar para retomar uma etapa pendente.");
        void read();
      }
    };
    actions.current = { read, start, resume, cancel };
    setJobs([]);
    setCapability(null);
    setBudget(null);
    setError("");
    setMessage("");
    setBusy(false);
    setUncertain(pending.current !== null);
    setReconciled(false);
    if (visible()) void read();
    document.addEventListener("visibilitychange", visibility);
    return () => {
      controller.abort();
      intent = null;
      if (timer) clearTimeout(timer);
      document.removeEventListener("visibilitychange", visibility);
      actions.current = null;
    };
  }, [systemId, accessToken, active]);
  const money = (value: number) =>
    (value / 1000000).toLocaleString("pt-BR", {
      style: "currency",
      currency: "BRL",
      minimumFractionDigits: 2,
      maximumFractionDigits: 6,
    });
  return (
    <Box borderWidth="1px" rounded="md" p="4">
      <Heading size="sm" mb="3">
        Amostra sintética da IA
      </Heading>
      <Text mb="3">
        Mantenha esta tela aberta para autorizar as etapas. Uma operação já
        iniciada pode concluir e gerar cobrança após fechar a tela.
      </Text>
      <Text fontSize="sm" mb="3">
        Este teste usa uma mensagem interna sem dados de clientes. O
        licenciamento e a liberação do plano continuam pelo iFlex.
      </Text>
      {error && <OktonAlert status="error">{error}</OktonAlert>}
      {message && <Text role="status">{message}</Text>}
      {capability && !capability.canStart && (
        <Text>
          Amostra desabilitada. A ativação operacional ainda precisa ser
          concluída.
        </Text>
      )}
      {budget?.usage && (
        <Box my="3">
          <Heading size="xs">Orçamento global — total acumulado</Heading>
          <Text>
            Confirmado: {money(budget.usage.confirmedMicros)} · Reservado:{" "}
            {money(budget.usage.reservedMicros)} · Pendente:{" "}
            {money(budget.usage.pendingMicros)}
          </Text>
          <Text>
            Disponível: {money(budget.usage.availableMicros)}. Estes valores
            incluem todas as solicitações do piloto.
          </Text>
        </Box>
      )}
      <Stack direction="row" my="3">
        <Button
          size="sm"
          onClick={() => void actions.current?.start()}
          isDisabled={
            busy ||
            !active ||
            !accessToken ||
            !capability?.canStart ||
            jobs.some(running) ||
            uncertain
          }
        >
          Iniciar amostra
        </Button>
        <Button
          size="sm"
          onClick={() => void actions.current?.read()}
          isDisabled={busy || !active || !accessToken}
        >
          Recarregar
        </Button>
        {uncertain && (
          <Button
            size="sm"
            onClick={() => void actions.current?.start()}
            isDisabled={
              busy || !reconciled || !capability?.canStart || jobs.some(running)
            }
          >
            Reenviar solicitação
          </Button>
        )}
      </Stack>
      {jobs.map((job) => (
        <Box key={job.id} borderTopWidth="1px" py="3">
          <Text fontWeight="bold">{labels[job.state]}</Text>
          <Text fontSize="xs">Solicitação {job.id}</Text>
          {job.cancelRequested && (
            <Text>
              Cancelamento solicitado; cobrança já iniciada pode permanecer
              pendente.
            </Text>
          )}
          <Stack direction="row" mt="2">
            {waiting(job) && (
              <Button
                size="sm"
                isDisabled={
                  busy || !active || !accessToken || !capability?.canStart
                }
                onClick={() => void actions.current?.resume(job.id)}
              >
                Continuar
              </Button>
            )}
            {running(job) && (
              <Button
                size="sm"
                isDisabled={busy || !active || !accessToken}
                onClick={() => void actions.current?.cancel(job.id)}
              >
                Cancelar amostra
              </Button>
            )}
          </Stack>
        </Box>
      ))}
    </Box>
  );
};
